import React, { createContext, useContext, useEffect, useState } from "react";
import {
  deleteProfilePhoto,
  getInfo,
  updateAboutMe,
  updateAddress,
  updateAvailability,
  updateBasicInfo,
  updateSessionFormats,
  updateContact,
  updateLinksInfo,
  updateOfficeInfo,
  updateProfilePhoto,
  downloadPreviousProfilePhoto,
} from "../services/basicInfo.services";
import { ERROR, LOADING, SUCCESS } from "../constants/common";
import { useAlert } from "./Alert";
import { useAuth } from "./Auth";
import { useCommon } from "./common";
import { geocodeAddress } from "../services/common.services";
import { updateNetworkingStatus } from "../services/home.services";

const BasicInfoContext = createContext({});

export const BasicInfoContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const { getStatebyId } = useCommon();
  const [basicInfoState, setInfoState] = useState({
    fetchStatus: "",
    updateBasicInfoStatus: "",
    updateAboutMeStatus: "",
    updateContactStatus: "",
    updateOfficeInfoStatus: "",
    updateAddressStatus: "",
    updateLinkstatus: "",
    updatePhotoStatus: "",
    updateAvailabilityStatus: "",
    data: {},
  });

  const { isLoggedIn } = useAuth();

  const handleState = (obj) => {
    setInfoState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const fetchInfo = async () => {
    handleState({ fetchStatus: LOADING });
    try {
      const response = await getInfo();
      
      handleState({
        data: response.data.data || {},
        fetchStatus: SUCCESS,
      });
    } catch (error) {
      handleState({ fetchStatus: ERROR });
      Promise.reject(error);
    }
  };

  const updateSessionFormat = async (data) => {
    handleState({ updateBasicInfoStatus: LOADING });
    try {
      const result = await updateSessionFormats(data);
      handleState({
        updateBasicInfoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateBasicInfoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editBasicInfo = async (data) => {
    handleState({ updateBasicInfoStatus: LOADING });
    try {
      await updateBasicInfo(data);
      handleState({
        updateBasicInfoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateBasicInfoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editAboutMe = async (data) => {
    handleState({ updateAboutMeStatus: LOADING });
    try {
      const response = await updateAboutMe(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null);
        handleState({ updateAboutMeStatus: ERROR });
        return;
      }
      handleState({
        updateAboutMeStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateAboutMeStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editContact = async (data) => {
    handleState({ updateContactStatus: LOADING });
    try {
      await updateContact(data);
      handleState({
        updateContactStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateContactStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editOfficeInfo = async (data) => {
    handleState({ updateOfficeInfoStatus: LOADING });
    try {
      await updateOfficeInfo(data);
      handleState({
        updateOfficeInfoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateOfficeInfoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editAddress = async (data) => {
    handleState({ updateAddressStatus: LOADING });
    try {
      if (!(data?.lat && data?.lng)) {
        const state = await getStatebyId(data?.state);
        const updatedAddress = await geocodeAddress(
          `${data?.address1}, ${data?.city}, ${state?.name}, ${data?.zip_code}`
        );
        data = { ...data, lat: updatedAddress?.lat, lng: updatedAddress?.lng };
      }
      await updateAddress(data);
      handleState({
        updateAddressStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateAddressStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editLinksInfo = async (data) => {
    handleState({ updateLinkstatus: LOADING });
    try {
      await updateLinksInfo(data);
      handleState({
        updateLinkstatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateLinkstatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editProfilePhoto = async (data) => {
    handleState({ updatePhotoStatus: LOADING });
    try {
      await updateProfilePhoto(data);
      handleState({
        updatePhotoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updatePhotoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const downloadProfilePhoto = async (data) => {
    handleState({ updatePhotoStatus: LOADING });
    try {
      const fileName = data.substring(data.lastIndexOf("/") + 1);
      const response = await downloadPreviousProfilePhoto(fileName);
      // Extract image data from response
      const { content, type, filename } = response.data?.data;

      if (!content) {
        throw new Error("No image content received from server");
      }

      // Convert base64 to blob
      const byteCharacters = atob(content);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      // Create Blob and File objects
      const blob = new Blob(byteArrays, { type: type });
      const file = new File([blob], filename, { type: type });

      handleState({ updatePhotoStatus: SUCCESS });
      return file;
    } catch (error) {
      handleState({ updatePhotoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const removeProfilePhoto = async () => {
    handleState({ updatePhotoStatus: LOADING });
    try {
      await deleteProfilePhoto();
      handleState({
        updatePhotoStatus: SUCCESS,
      });
      sendAlert("Removed successfully", "success");
    } catch (error) {
      handleState({ updatePhotoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editAvailability = async (data) => {
    handleState({ updateAvailabilityStatus: LOADING });
    try {
      await updateAvailability(data);
      handleState({
        updateAvailabilityStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateAvailabilityStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editNetworkingStatus = async (data, account_type) => {
    handleState({ updateNetworkingStatus: LOADING });
    try {
      await updateNetworkingStatus(data, account_type);
      handleState({
        updateNetworkingStatus: SUCCESS,
      });
      sendAlert(
        "Thanks for sharing your networking status! This helps others know when you’re open to connect",
        "success"
      );
    } catch (error) {
      handleState({ updateNetworkingStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const updateInfoState = (field, value) => {
    handleState({ [field]: value });
  };

  useEffect(() => {
    handleState({
      data: {},
    });
  }, [isLoggedIn]);

  const state = {
    ...basicInfoState,
    fetchInfo,
    editBasicInfo,
    updateSessionFormat,
    editAboutMe,
    editContact,
    editOfficeInfo,
    editAddress,
    editLinksInfo,
    editProfilePhoto,
    downloadProfilePhoto,
    removeProfilePhoto,
    editAvailability,
    updateInfoState,
    editNetworkingStatus,
  };

  return (
    <BasicInfoContext.Provider value={state}>
      {children}
    </BasicInfoContext.Provider>
  );
};

export const useBasicInfo = () => useContext(BasicInfoContext);
